import * as React from 'react';
import * as ReactDOM from 'react-dom';
import cx from 'classnames';

// componenets
import icons from '../icons';

const ModalOverlayClass = 'modal__overlay';

export interface ModalChildProps {
    handleToggleVisibility: () => void;
}

export interface ModalContainerProps {
    children: (ModalChildProps) => React.ReactNode;
    onDismiss?: (...args: any[]) => any;
    title?: string;
    innerContent: (...args: any[]) => React.ReactNode | React.ReactNode[];
    className?: string;
    contentClassName?: string;
    enableDismiss?: boolean;
    container?: Element;
    showByDefault?: boolean;
}

export interface ModalProps extends ModalChildProps, Partial<ModalContainerProps> {
    container?: Element;
    handleOverlayClick: (event: React.MouseEvent<HTMLElement>) => void;
}


// renders Modal and passes visibility handler down to children
const ModalContainer: React.FunctionComponent<ModalContainerProps> = ({ container, innerContent, title, onDismiss, className, children, contentClassName, enableDismiss = true, showByDefault = false }) => {

    const [isVisible, setIsVisible] = React.useState(showByDefault);
    const handleToggleVisibility = () => setIsVisible(!isVisible);
    const handleOverlayClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if ((event.target as HTMLElement).classList.contains(ModalOverlayClass)) {
            enableDismiss && handleToggleVisibility();
        }
    };

    React.useEffect(() => {
        if (onDismiss) {
            return () => onDismiss();
        }
    }, []);

    return (
        <>
            {
                isVisible &&
                <Modal
                    innerContent={innerContent}
                    title={title}
                    handleToggleVisibility={handleToggleVisibility}
                    handleOverlayClick={handleOverlayClick}
                    className={className}
                    enableDismiss={enableDismiss}
                    container={container}
                    contentClassName={contentClassName}
                />
            }
            {children({ handleToggleVisibility })}
        </>
    );
};

// creates a portal attaching the modal to the document.body
const Modal: React.FunctionComponent<ModalProps> = ({ children, container, ...props }) => (
    ReactDOM.createPortal(
        <div className={cx(ModalOverlayClass, props.className, { 'modal__overlay--fullscreen': !container })} onClick={props.handleOverlayClick}>
            <ModalContent {...props} />
            {children}
        </div>,
        container || document.body
    )
);

// handles displaying inner content of modal
const ModalContent: React.FunctionComponent<Partial<ModalProps>> = ({ title, innerContent, handleToggleVisibility, enableDismiss, contentClassName }) => (
    <div className={cx('modal__container', contentClassName)}>
        <div className="modal__header">
            {
                title && <span className="modal__title">{title}</span>
            }
            {
                enableDismiss && <button className="modal__closeBtn" onClick={handleToggleVisibility}>{icons.cancel('#B3B3B3', 1)}</button>
            }
        </div>
        <div className="modal__content">
            {innerContent(handleToggleVisibility)}
        </div>
    </div>
);

export default ModalContainer;



// WEBPACK FOOTER //
// ./src/components/Modal.tsx